import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Home as HomeIcon,
  User,
  Heart,
  ShoppingBag,
  Menu as MenuIcon,
  X,
  Shirt,
  LogOut,
  LogIn,
  ShieldCheck,
  Package,
  Sparkles,
  ShieldAlert,
} from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { CATEGORIES } from "@/lib/categories";
import ReportModal from "@/components/ReportModal";

const NavItem = ({ to, icon: Icon, label, testId, badge, onClick }) => {
  // Compose the a11y label so screen readers announce e.g. "Cart, 3 items"
  // instead of just "Cart".
  const ariaLabel = badge > 0
    ? `${label}, ${badge} ${label.toLowerCase() === "cart" ? "item" : "item"}${badge === 1 ? "" : "s"}`
    : label;
  const content = ({ isActive }) => (
    <>
      <div className="relative">
        <Icon aria-hidden="true" className={cn("h-5 w-5", isActive && "text-black")} strokeWidth={isActive ? 2.2 : 1.6} />
        {badge > 0 && (
          <span
            data-testid={`${testId}-badge`}
            aria-hidden="true"
            className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#B8912B] px-1 text-[9px] font-bold text-white"
          >
            {badge}
          </span>
        )}
      </div>
      <span>{label}</span>
      <span
        aria-hidden="true"
        className={cn(
          "mt-0.5 h-[2px] w-6 transition-all duration-300",
          isActive ? "bg-[#D4AF37]" : "bg-transparent"
        )}
      />
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        aria-label={ariaLabel}
        className="relative flex min-h-[48px] min-w-[48px] flex-col items-center justify-center gap-0.5 px-3 py-1.5 text-[10px] font-medium uppercase tracking-widest text-gray-400 hover:text-gray-700"
      >
        {content({ isActive: false })}
      </button>
    );
  }
  return (
    <NavLink
      to={to}
      data-testid={testId}
      aria-label={ariaLabel}
      className={({ isActive }) =>
        cn(
          "relative flex min-h-[48px] min-w-[48px] flex-col items-center justify-center gap-0.5 px-3 py-1.5 text-[10px] font-medium uppercase tracking-widest transition-colors",
          isActive ? "text-black" : "text-gray-400 hover:text-gray-700"
        )
      }
    >
      {({ isActive }) => (
        <span aria-current={isActive ? "page" : undefined} className="contents">
          {content({ isActive })}
        </span>
      )}
    </NavLink>
  );
};

const MenuDrawer = ({ open, onClose, onReport }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const go = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 transition-opacity",
        open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
      )}
      data-testid="menu-drawer-overlay"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className={cn(
          "absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col bg-white pt-safe pb-safe shadow-2xl transition-transform duration-300",
          open ? "translate-x-0" : "translate-x-full"
        )}
        data-testid="menu-drawer"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex flex-col leading-none">
            <span className="font-heading text-xl font-semibold">Royal</span>
            <span className="-mt-1 font-heading text-xl font-semibold text-[#D4AF37]">Lady</span>
          </div>
          <button
            type="button"
            data-testid="menu-drawer-close"
            onClick={onClose}
            aria-label="Close menu"
            className="grid h-11 w-11 place-items-center text-black hover:text-[#D4AF37]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {user && (
          <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4">
            <div className="grid h-11 w-11 place-items-center bg-[#FBF6E4] font-heading text-lg text-[#B8912B]">
              {user.name?.[0]?.toUpperCase() || "R"}
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-black">{user.name}</div>
              <div className="text-[11px] text-gray-500">{user.email}</div>
            </div>
            {user.role === "admin" && (
              <span className="inline-flex items-center gap-1 bg-[#D4AF37] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                <ShieldCheck className="h-3 w-3" /> Admin
              </span>
            )}
          </div>
        )}

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          <SectionTitle>Shop</SectionTitle>
          {CATEGORIES.map((c) => (
            <MenuLink
              key={c.key}
              icon={Shirt}
              label={c.label}
              testId={`menu-cat-${c.key}`}
              onClick={() => go(`/category/${c.key}`)}
            />
          ))}

          <SectionTitle>My Royal Lady</SectionTitle>
          <MenuLink icon={User} label="My profile" testId="menu-profile" onClick={() => go("/profile")} />
          <MenuLink icon={Package} label="My orders" testId="menu-orders" onClick={() => go("/profile")} />
          <MenuLink icon={Heart} label="Wishlist" testId="menu-wishlist" onClick={() => go("/wishlist")} />
          <MenuLink icon={ShoppingBag} label="Bag" testId="menu-cart" onClick={() => go("/cart")} />

          <SectionTitle>Offers</SectionTitle>
          <div className="mx-3 my-2 flex items-center gap-3 border border-[#D4AF37] bg-[#FBF6E4] px-3 py-3 text-sm">
            <Sparkles className="h-4 w-4 text-[#B8912B]" />
            <div>
              <div className="font-medium text-[#8b6c15]">Free shipping ₹999+</div>
              <div className="text-[11px] text-[#8b6c15]/80">On every order, everyday.</div>
            </div>
          </div>

          {user?.role === "admin" && (
            <>
              <SectionTitle>Manage</SectionTitle>
              <MenuLink icon={ShieldCheck} label="Admin panel" testId="menu-admin" onClick={() => go("/admin")} />
            </>
          )}

          <SectionTitle>Support</SectionTitle>
          <MenuLink
            icon={ShieldAlert}
            label="Report a problem"
            testId="menu-report-issue"
            onClick={() => { onClose(); onReport && onReport(); }}
          />
        </nav>

        <div className="border-t border-gray-100 px-3 py-3">
          {user ? (
            <button
              data-testid="menu-logout-btn"
              onClick={() => {
                logout();
                onClose();
                navigate("/");
              }}
              className="flex w-full items-center justify-center gap-2 border border-gray-200 py-3 text-xs font-semibold uppercase tracking-widest text-black hover:border-black"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          ) : (
            <button
              data-testid="menu-login-btn"
              onClick={() => go("/login")}
              className="flex w-full items-center justify-center gap-2 bg-black py-3 text-xs font-semibold uppercase tracking-widest text-white"
            >
              <LogIn className="h-4 w-4" /> Sign in
            </button>
          )}
        </div>
      </aside>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <div className="mt-3 px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-gray-400">
    {children}
  </div>
);

const MenuLink = ({ icon: Icon, label, testId, onClick }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className="flex min-h-[48px] w-full items-center gap-3 px-3 py-3 text-sm text-black hover:bg-[#FAFAFA]"
  >
    <Icon aria-hidden="true" className="h-4 w-4 text-gray-500" />
    <span>{label}</span>
  </button>
);

const BottomNav = () => {
  const { cartCount, wishlist } = useShop();
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  return (
    <>
      <nav
        data-testid="bottom-nav"
        aria-label="Primary"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-100 bg-white/95 backdrop-blur-xl pb-safe shadow-[0_-4px_20px_rgba(0,0,0,0.04)]"
      >
        <div className="mx-auto flex max-w-screen-md items-stretch justify-between px-2 pt-1">
          <NavItem to="/" icon={HomeIcon} label="Home" testId="bottom-nav-home" />
          <NavItem to="/profile" icon={User} label="Profile" testId="bottom-nav-profile" />
          <NavItem to="/wishlist" icon={Heart} label="Wishlist" testId="bottom-nav-wishlist" badge={wishlist.length} />
          <NavItem to="/cart" icon={ShoppingBag} label="Cart" testId="bottom-nav-cart" badge={cartCount} />
          <NavItem icon={MenuIcon} label="Menu" testId="bottom-nav-menu" onClick={() => setMenuOpen(true)} />
        </div>
      </nav>
      <MenuDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onReport={() => setReportOpen(true)}
      />
      <ReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        subjectType="general"
      />
    </>
  );
};

export default BottomNav;
